import classNames from 'classnames';
import React, { useState, useCallback, useMemo, useRef, forwardRef } from 'react';
// import {
//     Draggable,
//     Droppable
// } from 'react-beautiful-dnd';
import Draggable from 'react-draggable'
import Checkbox from '../../wui-checkbox/src';
// import { KeyCode } from '../../wui-core/src';
import Icon from '../../wui-icon/src';
import Item from './item';
import PureRenderMixin from './PureRenderMixin';
import Search from './search';
import { TransferListProps, TransferItem, RenderItemObj, RenderCheckboxObj, TransferKeyType } from './iTransfer';
import { globalConfig } from '../../wui-provider/src';

const TransferList = forwardRef<HTMLDivElement, TransferListProps>((props, ref) => {
    const {
        prefixCls,
        dataSource = [],
        titleText = '',
        filter = '',
        checkedKeys = [],
        body = () => {},
        footer = () => {},
        showSearch = false,
        style,
        showCheckbox,
        draggable,
        fieldid,
        handleSelectAll,
        disabled = false,
        searchPlaceholder,
        notFoundContent,
        onDragStart,
        onDragMove,
        onDragEnd,
        onResetState,
        isDragging,
        draggingItem,
        dragOverIndex,
        dragFromList,
        dragOverList,
        isDraggingOutside,
        renderList,
        direction
    } = props;

    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
    const [absolutePosition, setAbsolutePosition] = useState<{ x: number, y: number } | null>(null);
    const [dragItemRect, setDragItemRect] = useState<{ width: number, height: number } | null>(null);
    const [startClient, setStartClient] = useState<{ x: number, y: number } | null>(null);
    const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const listContentRef = useRef<HTMLDivElement>(null);
    const isDraggingThis = useRef<boolean>(false);
    const isShowSelected = useRef<boolean>(true);
    const hasDraggedRef = useRef<boolean>(false); // 是否真正发生了拖拽
    const dragDistanceRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 }); // 记录拖拽距离

    const matchFilter = useCallback((text: string, item: TransferItem) => {
        if (props.filterOption) {
            return props.filterOption(filter, item);
	    }
	    return text.indexOf(filter) >= 0;
    }, [filter, props.filterOption]);

    const getCheckStatus = useCallback((filteredDataSource: TransferItem[]) => {
	    if (checkedKeys && checkedKeys.length === 0) {
            return 'none';
	    } else if (checkedKeys && filteredDataSource.length > 0 && filteredDataSource.every(item => checkedKeys.indexOf(item.key) >= 0)) {
            return 'all';
        }
        return 'part';
    }, [checkedKeys]);

    const handleSelect = useCallback((selectedItem: TransferItem) => {
        const result = checkedKeys?.some((key) => key === selectedItem.key);
        props.handleSelect?.(selectedItem, result);
    }, [checkedKeys, props.handleSelect]);

    const handleFilter = useCallback((e: string) => {
        props.handleFilter?.(e);
    }, [props.handleFilter]);

    const handleClear = useCallback(() => {
        props.handleClear?.();
    }, [props.handleClear]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
        props.handleScroll && props.handleScroll(e);
    }, [props.handleScroll]);

    const renderItem = useCallback((item: TransferItem) => {
        const renderResult: React.ReactElement | RenderItemObj | string | void = (props.render || (() => {}))(item);
        const isPlain = renderResult && !React.isValidElement(renderResult) && Object.prototype.toString.call(renderResult) === '[object Object]';
	    return {
            renderedText: isPlain ? (renderResult as RenderItemObj).value : typeof renderResult === 'string' ? renderResult : '',
            renderedEl: isPlain ? (renderResult as RenderItemObj).label : renderResult as string,
        };
    }, [props.render]);

    const renderCheckbox = useCallback(({ prefixCls, filteredDataSource, checked, checkPart, disabled }: RenderCheckboxObj) => {
        let isAllDisabled = !(dataSource && dataSource.find(item => !item?.disabled));
        const checkAll = (!checkPart) && checked;
        prefixCls = "u";
	    const checkboxCls = classNames({
	        [`${prefixCls}-checkbox-indeterminate`]: checkPart,
	        [`${prefixCls}-checkbox-disabled`]: disabled,
	    });
	    return (
	        <Checkbox
                onChange={() => handleSelectAll?.(filteredDataSource, checkAll)}
	            className={checkboxCls}
	            checked={checkAll}
	            disabled={disabled || isAllDisabled}
	        />
	    );
    }, [dataSource, handleSelectAll]);

    // 处理拖拽开始
    const handleItemDragStart = useCallback((e: MouseEvent, item: TransferItem, index: number) => {
        if (item.disabled || !draggable) return
        e.preventDefault()
        e.stopPropagation()
        isShowSelected.current = true
        hasDraggedRef.current = false
        dragDistanceRef.current = { x: 0, y: 0 }

        const element = e.currentTarget as HTMLElement;

        if (element && listContentRef.current) {
            const elementRect = element.getBoundingClientRect()
            const mouseY = e.clientY;
            const mouseX = e.clientX;
            setStartClient(prev => ({
                ...prev,
                x: mouseX,
                y: mouseY
            }))

            const absolutePosition = {
                x: elementRect.left - listContentRef.current?.getBoundingClientRect().left,
                y: elementRect.top - listContentRef.current?.getBoundingClientRect().top
            }
            setAbsolutePosition(prev => ({
                ...prev,
                x: absolutePosition.x,
                y: absolutePosition.y
            }))

            setDraggingIndex(index)
            setDragItemRect(prev => ({
                ...prev,
                width: elementRect.width,
                height: elementRect.height
            }))
        }
        onDragStart?.(e, item, index)
    }, [draggable, onDragStart])

    // 处理拖拽
    const handleItemDrag = useCallback((e: MouseEvent, item: TransferItem) => {
        if (!isDragging || !startClient || !draggable) return

        const { x, y } = startClient;
        const diffX = e.clientX - x
        const diffY = e.clientY - y

        dragDistanceRef.current = { x: diffX, y: diffY }
        if (Math.abs(diffX) > 5 || Math.abs(diffY) > 5) {
            hasDraggedRef.current = true
        }
        if (Math.abs(diffX) < 5 && Math.abs(diffY) < 5) return

        const containerRect = listContentRef.current?.getBoundingClientRect()
        if (containerRect && !(e.clientX >= containerRect.left && e.clientX <= containerRect.right && e.clientY >= containerRect.top && e.clientY <= containerRect.bottom)) {
            isShowSelected.current = false
        }

        onDragMove?.(e, item)
    }, [isDragging, onDragMove, startClient])

    // 处理拖拽结束
    const handleItemDragEnd = useCallback((e: MouseEvent, item: TransferItem) => {
        if (!draggable) return
        e.stopPropagation()
        isShowSelected.current = true
        if (!hasDraggedRef.current && Math.abs(dragDistanceRef.current.x) < 5 && Math.abs(dragDistanceRef.current.y) < 5) {
            handleSelect(item)
            onResetState?.()
        } else {
            onDragEnd?.(item)
        }
        setDraggingIndex(null)
        setAbsolutePosition(null)
        setDragItemRect(null)
        setStartClient(null)
        hasDraggedRef.current = false
        isDraggingThis.current = false
        dragDistanceRef.current = { x: 0, y: 0 }
        hasDraggedRef.current = false
    }, [onDragEnd, handleSelect])

    // 简化渲染逻辑，移除过度复杂的组件包装
    const renderDraggableItem = useCallback((item: TransferItem, index: number, checked: boolean, isDraggingThis: boolean, renderedText: string, renderedEl: any) => {
        // 只有在拖拽状态时才进行复杂的样式计算
        const itemStyle = isDraggingThis ? {
            display: 'block',
            zIndex: 5000,
            position: 'absolute' as const,
            top: absolutePosition ? `${absolutePosition.y}px` : undefined,
            left: absolutePosition ? `${absolutePosition.x}px` : undefined,
            width: dragItemRect ? `${dragItemRect.width}px` : undefined,
            height: dragItemRect ? `${dragItemRect.height}px` : undefined,
            cursor: 'grabbing',
        } : {
            display: (checked && !isShowSelected.current) ? 'none' : 'block'
        };

        const showTopPlaceholder = isDragging && (
            (draggingIndex !== null && dragOverIndex === index && dragOverIndex < draggingIndex) ||
            (dragOverIndex === -1 && index === 0)
        );

        const showBottomPlaceholder = isDragging &&
            dragOverIndex === index &&
            dragOverIndex !== null &&
            draggingIndex !== null &&
            dragOverIndex >= draggingIndex &&
            dragOverIndex !== -1;

        return (
            // @ts-ignore
            <div key={item.key}>
                {showTopPlaceholder && (
                    <div
                        className={`${prefixCls}-filter-column-placeholder-target`}
                        style={{ height: '32px' }}
                    />
                )}

                <Draggable
                    handle={`.${prefixCls}-drag-handle`}
                    bounds={false}
                    position={isDraggingThis ? undefined : {x: 0, y: 0}}
                    onStart={(e: MouseEvent) => handleItemDragStart(e, item, index)}
                    onDrag={(e: MouseEvent) => handleItemDrag(e, item)}
                    onStop={(e: MouseEvent) => handleItemDragEnd(e, item)}
                    disabled={item.disabled}
                >
                    <div style={itemStyle}>
                        <div
                            ref={(el) => {
                                if (el) itemRefs.current.set(item.key as string, el)
                            }}
                            className={`${prefixCls}-draggable-wrapper ${prefixCls}-drag-handle`}
                            style={{ position: 'relative' }}
                        >
                            <Item
                                item={item}
                                lazy={props.lazy}
                                renderedText={renderedText}
                                renderedEl={renderedEl}
                                checked={checked}
                                checkedKeys={checkedKeys}
                                prefixCls={prefixCls}
                                onClick={draggable ? undefined : handleSelect}
                                showCheckbox={showCheckbox as boolean}
                                isMultiDragSource={isDraggingThis && isDraggingOutside && dragFromList === 'left'}
                                draggingItemId=''
                            />
                        </div>
                    </div>
                </Draggable>

                {showBottomPlaceholder && (
                    <div
                        className={`${prefixCls}-filter-column-placeholder-target`}
                        style={{ height: '32px' }}
                    />
                )}
            </div>
        );
    }, [prefixCls, draggable, handleSelect, showCheckbox, props.lazy, isDragging, draggingIndex, dragOverIndex, dragFromList, isDraggingOutside, absolutePosition, dragItemRect, handleItemDragStart, handleItemDrag, handleItemDragEnd]);

    // 简化数据处理逻辑，合并filteredItems和showItems计算
    const showItems = useMemo(() => {
        return dataSource.map((item, index) => {
            if (!item) return null;

            // 简化item增强
            const enhancedItem = { ...item, disabled: disabled || item.disabled };
            const { renderedText, renderedEl } = renderItem(enhancedItem);

            // 早期过滤，减少后续处理
            if (filter && filter.trim() && typeof renderedText === 'string' && !matchFilter(renderedText, enhancedItem)) {
                return null;
            }

            const checked = checkedKeys.indexOf(item.key as string) >= 0;
            const isDraggingThis = draggingItem?.key === item.key;

            return renderDraggableItem(enhancedItem, index, checked, isDraggingThis, renderedText, renderedEl);
        }).filter(Boolean);
    }, [dataSource, disabled, renderItem, filter, matchFilter, checkedKeys, draggingItem?.key, renderDraggableItem]);

    // useMemo缓存defaultBodyContent
    const defaultBodyContent = useMemo(() => (
        <div
            ref={listContentRef}
            onScroll={event => handleScroll(event)}
            className={`${prefixCls}-content`}
        >
            <ul>
                {showItems}
            </ul>
            {draggable && dragFromList === 'right' && (
                <div className={`${prefixCls}-delete-selected ${isDragging && dragOverList === 'left' ? 'show' : ''}`}>
                    <div className={`${prefixCls}-del-btn`}>
                        <Icon type="uf-arrow-down-2"></Icon>
                        <span>移除已选</span>
                    </div>
                </div>
            )}
	    </div>
    ), [draggable, handleScroll, prefixCls, props.direction, isDragging, dragOverIndex, dataSource.length, showItems]);

    // 缓存props的关键部分，避免传递整个props对象
    const bodyProps = useMemo(() => ({
        dataSource,
        titleText,
        filter,
        checkedKeys,
        prefixCls,
        style,
        showSearch,
        showCheckbox,
        draggable,
        fieldid,
        disabled,
        searchPlaceholder,
        notFoundContent,
        direction
    }), [dataSource, titleText, filter, checkedKeys, prefixCls, style, showSearch, showCheckbox, draggable, fieldid, disabled, searchPlaceholder, notFoundContent, direction]);

    const footerProps = useMemo(() => ({
        dataSource,
        titleText,
        filter,
        checkedKeys,
        prefixCls,
        style,
        direction
    }), [dataSource, titleText, filter, checkedKeys, prefixCls, style, direction]);

    // useMemo缓存footerDom/bodyDom，避免传递完整props
    const footerDom = useMemo(() => footer(footerProps), [footer, footerProps]);
    const bodyDom = useMemo(() => body(bodyProps), [body, bodyProps]);
    const listCls = useMemo(() => classNames(prefixCls, {
        [`${prefixCls}-with-footer`]: !!footerDom,
        [`${prefixCls}-draggable`]: !!draggable
    }), [prefixCls, footerDom, draggable]);

    // 简化数据源计算，只在需要时计算
    const filteredDataSource = useMemo(() => {
        return dataSource.filter(item => {
            if (!item || item.disabled || disabled) return false;
            if (!filter || !filter.trim()) return true;
            const { renderedText } = renderItem(item);
            return typeof renderedText === 'string' && matchFilter(renderedText, item);
        });
    }, [dataSource, disabled, filter, renderItem, matchFilter]);

    const totalDataSource = useMemo(() => {
        return dataSource.filter(item => {
            if (!item) return false;
            if (!filter || !filter.trim()) return true;
            const { renderedText } = renderItem(item);
            return typeof renderedText === 'string' && matchFilter(renderedText, item);
        });
    }, [dataSource, filter, renderItem, matchFilter]);

    const checkStatus = getCheckStatus(filteredDataSource);
    const outerPrefixCls = (prefixCls as string).replace('-list', '');
    const fieldidProp = fieldid ? { fieldid } : {};
    const search = showSearch ? (
        <div className={`${prefixCls}-body-search-wrapper`}>
            <Search
                prefixCls={`${prefixCls}-search`}
                onChange={handleFilter}
                handleClear={handleClear}
                placeholder={searchPlaceholder}
                value={filter}
                disabled={disabled}
                {...fieldidProp}
            />
        </div>
    ) : null;

    // 渲染listBody
    const listBody = useMemo(() => {
        // 如果提供了renderList函数，使用自定义渲染
        if (renderList) {
            const renderListProps = {
                dataSource: filteredDataSource,
                onItemSelect: handleSelect,
                onItemSelectAll: handleSelectAll,
                direction,
                titleText,
                filter,
                checkedKeys,
                prefixCls,
                style,
                showSearch,
                showCheckbox,
                draggable,
                fieldid,
                disabled,
                searchPlaceholder,
                notFoundContent
            };
            let bodyContent: any = renderList(renderListProps) || defaultBodyContent;
            return (
                <div className={showSearch ? `${prefixCls}-body ${prefixCls}-body-with-search` : `${prefixCls}-body`}>
                    {search}
                    {bodyContent}
                </div>
            );
        }
        // 否则使用默认渲染
        return bodyDom || (
            <div className={showSearch ? `${prefixCls}-body ${prefixCls}-body-with-search` : `${prefixCls}-body`}>
                {search}
                {defaultBodyContent}
                <div className={`${prefixCls}-body-not-found ${dataSource.length === 0 ? "show" : ""}`}>
                    {notFoundContent}
                </div>
            </div>
        );
    }, [bodyDom, showSearch, prefixCls, search, defaultBodyContent, dataSource.length, notFoundContent, renderList, handleSelect, handleSelectAll, direction, filteredDataSource, titleText, filter, checkedKeys, style, showCheckbox, draggable, fieldid, disabled, searchPlaceholder]);

    // 渲染listFooter
    const listFooter = useMemo(() => footerDom ? (
        <div className={`${prefixCls}-footer`}>
            {footerDom}
        </div>
    ) : null, [footerDom, prefixCls]);

    // 渲染checkbox
    const renderedCheckbox = useMemo(() => renderCheckbox({
        prefixCls: outerPrefixCls,
        checked: checkStatus === 'all',
        checkPart: checkStatus === 'part',
        filteredDataSource,
        disabled: disabled,
    }), [renderCheckbox, outerPrefixCls, checkStatus, filteredDataSource, disabled]);

    // 渲染selectAllLabel
    const getSelectAllLabel = useCallback((checkedCount: number, totalCount: number, checkedKeys: TransferKeyType[], totalDataSource: any[]) : React.ReactNode => {
        const { selectAllLabel } = props;
        const { getGlobalDirection } = globalConfig();
		  const direction = getGlobalDirection();
	      if (selectAllLabel) {
		  return typeof selectAllLabel === 'function'
	              ? selectAllLabel({ checkedCount, totalCount, checkedKeys, totalDataSource })
	              : selectAllLabel;
	      }
		  if (direction === 'rtl') {
	          return (
	              <>
                    {`(${totalCount}/` + (checkedCount > 0 ? `${checkedCount})` : '0)')}
	              </>
	          );
	      }
	      return (
	          <>
	              {(checkedCount > 0 ? `(${checkedCount}/` : '(0/') + `${totalCount})`}
	          </>
	      );
    }, [props.selectAllLabel]);

	    return (
	        <div ref={ref} className={listCls} style={style}>
	            <div className={`${prefixCls}-header`}>
	                {showCheckbox ? renderedCheckbox : ''}
	                <span className={`${prefixCls}-header-selected`}>
	                    <span className={`${prefixCls}-header-title`}>
	                        {titleText}
	                    </span>
	                    <span className={`${prefixCls}-header-number`}>
                        {getSelectAllLabel(checkedKeys.length, totalDataSource.length, checkedKeys, totalDataSource)}
	                    </span>
	                </span>
	            </div>
	            {listBody}
	            {listFooter}
	        </div>
	    );
});

function areEqual(prevProps: TransferListProps, nextProps: TransferListProps) {
    return !PureRenderMixin.shouldComponentUpdate.call({ props: prevProps }, nextProps, {});
}

export default React.memo(TransferList, areEqual);